import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Container,
  FormField,
  Header,
  Input,
  Spinner,
  SpaceBetween,
  StatusIndicator,
  Button,
  Modal
} from '@cloudscape-design/components';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from '../../../context/authContext';
import { DocumentContext } from '../../../context/documentContext';
import { usePolicyContext } from '../../../context/policyContext';

interface PolicyCategoryItem {
  rule_id: string;
  rule_name: string;
  normalized_rule: string;
  obligation_type: string;
  rule_strength: string;
  priority: string;
  applies_to: string[];
  conditions: string[];
  exceptions: string[];
  thresholds: string[];
  timing: string[];
  source_text: string[];
  source_section: string;
}

interface PolicyResponseText {
  document_type: string;
  document_summary: string;
  policy_categories:
    | PolicyCategoryItem[]
    | Record<string, PolicyCategoryItem | PolicyCategoryItem[]>;
}

interface PolicyBatchResult {
  batchNumber: number;
  startPageNo: number;
  endPageNo: number;
  completeTokenCount: number;
  contextCount: number;
  contextIds: string[];
  inputTokens: number;
  outputTokens: number;
  responseText: PolicyResponseText;
}

interface PolicyJsonFile {
  documentId: string;
  customerId: string;
  contextCount: number;
  batchCount: number;
  batchResults: PolicyBatchResult[];
}

interface PolicyDetailsState {
  policyName: string;
  version: string;
  type: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getPolicyNameFromFileName = (fileName: string | undefined) => {
  if (!fileName) {
    return '';
  }

  return fileName.replace(/\.[^/.]+$/, '');
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string');
};

const toPolicyCategoryItem = (value: unknown): PolicyCategoryItem[] => {
  if (!isRecord(value)) {
    return [];
  }

  const rule_id = typeof value.rule_id === 'string' ? value.rule_id : '';
  const rule_name = typeof value.rule_name === 'string' ? value.rule_name : '';
  const normalized_rule =
    typeof value.normalized_rule === 'string' ? value.normalized_rule : '';
  const obligation_type =
    typeof value.obligation_type === 'string' ? value.obligation_type : '';
  const rule_strength =
    typeof value.rule_strength === 'string' ? value.rule_strength : '';
  const priority = typeof value.priority === 'string' ? value.priority : '';
  const applies_to = toStringArray(value.applies_to);
  const conditions = toStringArray(value.conditions);
  const exceptions = toStringArray(value.exceptions);
  const thresholds = toStringArray(value.thresholds);
  const timing = toStringArray(value.timing);
  const source_text =
    value.source_text !== undefined
      ? toStringArray(value.source_text)
      : typeof value.original_rule_text === 'string'
        ? [value.original_rule_text]
        : typeof value.originalText === 'string'
          ? [value.originalText]
          : typeof value.text === 'string'
            ? [value.text]
            : [];
  const source_section =
    typeof value.source_section === 'string' ? value.source_section : '';

  if (!rule_name && !normalized_rule && source_text.length === 0) {
    return [];
  }

  return [
    {
      rule_id,
      rule_name,
      normalized_rule,
      obligation_type,
      rule_strength,
      priority,
      applies_to,
      conditions,
      exceptions,
      thresholds,
      timing,
      source_text,
      source_section
    }
  ];
};

const normalizePolicyCategories = (value: unknown): PolicyCategoryItem[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => toPolicyCategoryItem(item));
  }

  if (isRecord(value)) {
    const directMatch = toPolicyCategoryItem(value);
    if (directMatch.length > 0) {
      return directMatch;
    }

    return Object.values(value).flatMap((item) =>
      normalizePolicyCategories(item)
    );
  }

  return [];
};

const extractPolicyCategories = (
  data: PolicyJsonFile | undefined
): PolicyCategoryItem[] => {
  const categories = data?.batchResults?.[0]?.responseText?.policy_categories;
  return normalizePolicyCategories(categories);
};

const getErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Unable to load policy document.';
};

interface PolicyCardField {
  label: string;
  value: string;
}

const buildPolicyCardFields = (item: PolicyCategoryItem): PolicyCardField[] => [
  {
    label: 'Rule Name',
    value: item.rule_name || 'N/A'
  },
  {
    label: 'Normalised Rule',
    value: item.normalized_rule || 'N/A'
  },
  {
    label: 'Source Text',
    value: item.source_text.length > 0 ? item.source_text.join('\n') : 'N/A'
  }
];

const PolicyDocumentViewer = () => {
  const { auth, token } = useContext(UserContext);
  const { allDocuments, setCurrentDocId } = useContext(DocumentContext);
  const { getPolicies, getPoliciesLoading, addPolicy, updatePolicy } =
    usePolicyContext();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [policyCategories, setPolicyCategories] = useState<
    PolicyCategoryItem[]
  >([]);
  const [ignoredRules, setIgnoredRules] = useState<Record<number, boolean>>({});
  const [saveModalVisible, setSaveModalVisible] = useState<boolean>(false);
  const [policyDetails, setPolicyDetails] = useState<PolicyDetailsState>({
    policyName: '',
    version: '',
    type: ''
  });

  const documentId = searchParams.get('documentId');

  const currentDocument = useMemo(
    () => allDocuments.find((document) => document._id === documentId) ?? null,
    [allDocuments, documentId]
  );

  useEffect(() => {
    if (!documentId) {
      return;
    }

    setCurrentDocId(documentId);
  }, [documentId, setCurrentDocId]);

  useEffect(() => {
    if (!currentDocument) {
      return;
    }

    setPolicyDetails((previousValue) => ({
      ...previousValue,
      policyName:
        previousValue.policyName ||
        getPolicyNameFromFileName(currentDocument.documentFileName),
      version:
        previousValue.version || currentDocument.documentMetaData?.version || ''
    }));
  }, [currentDocument]);

  useEffect(() => {
    let cancelled = false;

    const loadPolicyJson = async () => {
      if (!token || !documentId) {
        return;
      }

      setLoading(true);
      setError('');

      let lastError = 'Unable to fetch the policy JSON file.';

      try {
        const presignedUrl = await axios.post(
          `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getPolicyJson`,
          { documentId },
          auth
        );
        const downloadUrl = presignedUrl?.data?.url;

        if (!downloadUrl || downloadUrl === 'locked') {
          throw new Error('No policy JSON presigned URL was returned.');
        }

        const response = await axios.get(downloadUrl, {
          responseType: 'json'
        });

        if (cancelled) {
          return;
        }

        setPolicyCategories(extractPolicyCategories(response.data));
        setPolicyDetails((previousValue) => ({
          ...previousValue,
          policyName:
            previousValue.policyName ||
            getPolicyNameFromFileName(currentDocument?.documentFileName),
          version:
            previousValue.version ||
            currentDocument?.documentMetaData?.version ||
            '',
          type:
            previousValue.type ||
            response.data?.batchResults?.[0]?.responseText?.document_type ||
            ''
        }));
        setIgnoredRules({});
        setLoading(false);
        return;
      } catch (fetchError) {
        lastError = getErrorMessage(fetchError);
      }

      if (!cancelled) {
        setPolicyCategories([]);
        setError(lastError);
        setLoading(false);
      }
    };

    loadPolicyJson();

    return () => {
      cancelled = true;
    };
  }, [auth, documentId, token]);

  const handleIgnoreRuleChange = (index: number, checked: boolean) => {
    setIgnoredRules((previousValue) => ({
      ...previousValue,
      [index]: checked
    }));
  };

  const handlePolicyDetailsChange = (
    field: keyof PolicyDetailsState,
    value: string
  ) => {
    setPolicyDetails((previousValue) => ({
      ...previousValue,
      [field]: value
    }));
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6">
      <Container
        header={
          <Header
            variant="h1"
            description={
              currentDocument?.documentFileName || documentId || 'Policy viewer'
            }
          >
            Policy Document Viewer
          </Header>
        }
        footer={
          <div className="flex justify-end">
            <Button onClick={() => setSaveModalVisible(true)}>
              Save Policy
            </Button>
          </div>
        }
      >
        <SpaceBetween size="l">
          {!documentId ? (
            <Alert type="error">
              Missing `documentId` in the search params.
            </Alert>
          ) : null}

          {error ? <Alert type="error">{error}</Alert> : null}

          <Header counter={`(${policyCategories.length})`}>
            Policy Categories
          </Header>

          {loading ? (
            <Box textAlign="center" padding={{ vertical: 'xl' }}>
              <Spinner size="large" />
            </Box>
          ) : null}

          {!loading && policyCategories.length === 0 ? (
            <Box margin={{ vertical: 'xs' }} textAlign="center">
              {documentId
                ? 'No policy categories found.'
                : 'Policy categories will appear here.'}
            </Box>
          ) : null}

          {!loading ? (
            <div className="grid grid-cols-1 gap-4">
              {policyCategories.map((item, index) => {
                const fields = buildPolicyCardFields(item);
                const isIgnored = ignoredRules[index] ?? false;

                return (
                  <Container
                    key={`${item.rule_name}-${index}`}
                    header={
                      <Header
                        variant="h2"
                        actions={
                          <StatusIndicator
                            type={isIgnored ? 'warning' : 'success'}
                          >
                            {isIgnored ? 'Ignored' : 'Active'}
                          </StatusIndicator>
                        }
                      >
                        {fields[0].value}
                      </Header>
                    }
                  >
                    <SpaceBetween size="m">
                      {fields.map((field) => (
                        <div key={field.label}>
                          <Box
                            variant="awsui-key-label"
                            color="text-body-secondary"
                          >
                            {field.label}
                          </Box>
                          <div className="whitespace-pre-wrap break-words text-sm leading-6">
                            {field.value}
                          </div>
                        </div>
                      ))}

                      <Checkbox
                        checked={isIgnored}
                        onChange={({ detail }) =>
                          handleIgnoreRuleChange(index, detail.checked)
                        }
                      >
                        Ignore rule
                      </Checkbox>
                    </SpaceBetween>
                  </Container>
                );
              })}
            </div>
          ) : null}
        </SpaceBetween>
      </Container>
      <Modal
        visible={saveModalVisible}
        onDismiss={() => setSaveModalVisible(false)}
        header={<Header>Save Policy</Header>}
      >
        <SpaceBetween size="l">
          <Box>Save this policy as a set of rules (Text needs to change)</Box>
          <FormField label="Policy Name">
            <Input
              value={policyDetails.policyName}
              onChange={({ detail }) =>
                handlePolicyDetailsChange('policyName', detail.value)
              }
            />
          </FormField>

          <FormField label="Version">
            <Input
              value={policyDetails.version}
              onChange={({ detail }) =>
                handlePolicyDetailsChange('version', detail.value)
              }
            />
          </FormField>

          <FormField label="Type">
            <Input
              value={policyDetails.type}
              onChange={({ detail }) =>
                handlePolicyDetailsChange('type', detail.value)
              }
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </div>
  );
};

export default PolicyDocumentViewer;
